import { t, Trans } from '@lingui/macro'
import { Grid } from '@material-ui/core'
import { connect, Field, Formik } from 'formik'
import React, { useCallback, useMemo } from 'react'
import {
  EAccountStatus,
  EBillingPlan,
  getAccountStatus,
  IAccount,
} from 'shopify-payment-core'

import { AccountBillingPlan } from '../atoms/AccountBillingPlan'
import { Brick } from '../atoms/Brick'
import { FormDateField } from '../atoms/FormDateField'
import { FormTextField } from '../atoms/FormTextField'
import { SubmitButton } from '../atoms/SubmitButton'
import { formatDateInput } from '../helpers/formats'
import { useAccountUpdate } from '../hooks/useAccountUpdate'
import { i18n } from '../i18n'

interface IProps {
  account: IAccount
  noGutter?: boolean
}

interface IFormValues
  extends Pick<
    IAccount,
    'billingPlan' | 'billingIdentity' | 'invoiceDueDate' | 'invoicePaidAt'
  > {}

const WithBillingPlan = connect<
  { plan?: EBillingPlan; nonFree?: boolean },
  IFormValues
>(({ formik, plan, nonFree, children }) => {
  if (nonFree === true && formik.values.billingPlan === EBillingPlan.FREE) {
    return null
  }
  if (plan && formik.values.billingPlan !== plan) {
    return null
  }
  return children as ReactNode
})

export const AccountBillingAdmin: React.FC<IProps> = ({
  account,
  noGutter,
}) => {
  const update = useAccountUpdate(
    account,
    i18n._(t`Aktualizováno nastavení fakturace`),
  )
  const onSubmit = useCallback((values: IFormValues) => {
    const {
      billingPlan,
      billingIdentity,
      invoiceDueDate,
      invoicePaidAt,
    } = values
    if (billingPlan !== EBillingPlan.FREE) {
      const accountStatus = getAccountStatus({ ...account, ...values })
      if (
        accountStatus === EAccountStatus.EXPIRED &&
        !invoicePaidAt &&
        !window.confirm(
          i18n._(
            t`Trial platby pro tento účet jsou vyčerpány. Dokud nebude vyplněno datum platby, nebude možné provádět další platby. Opravdu chceš pokračovat?`,
          ),
        )
      ) {
        return
      }
    } else {
      // remove invoice & pay date from free account
      update({
        billingPlan,
        billingIdentity,
        invoicePaidAt: null,
        invoiceDueDate: null,
      })
      return
    }
    update({ billingPlan, billingIdentity, invoiceDueDate, invoicePaidAt })
  }, [])

  const validate = useCallback((values: IFormValues) => {
    if (values.billingPlan !== EBillingPlan.FREE && !values.invoiceDueDate) {
      return {
        invoiceDueDate: i18n._(t`Datum splatnosti musí být vyplněno`),
      }
    }
    return
  }, [])

  const initialValues: IFormValues = useMemo(
    () => ({
      billingPlan: account.billingPlan || EBillingPlan.FREE,
      billingIdentity: account.billingIdentity || '',
      invoiceDueDate: account.invoiceDueDate || '',
      invoicePaidAt: account.invoicePaidAt || '',
    }),
    [],
  )

  return (
    <Brick title={i18n._(t`Fakturace`)} admin={true} noGutter={noGutter}>
      <Formik<IFormValues>
        onSubmit={onSubmit}
        initialValues={initialValues}
        isInitialValid={() => !validate(initialValues)}
        validate={validate}
      >
        <Grid container alignItems="flex-start">
          <Grid item lg={6}>
            <Field
              component={AccountBillingPlan}
              name="billingPlan"
              allowFree
            />
            <br />
            <SubmitButton>
              <Trans>Potvrdit nastavení</Trans>
            </SubmitButton>
          </Grid>
          <WithBillingPlan nonFree={true}>
            <Grid item container lg={6} direction="column">
              <WithBillingPlan plan={EBillingPlan.MONTHLY}>
                <Grid item>
                  <Field
                    label={<b>{i18n._(t`Příští fakturace`)} *</b>}
                    component={FormDateField}
                    name="invoiceDueDate"
                    helperText={i18n._(t`Povinné datum fakturace se bude automaticky zvyšovat na měsíční bázi poté
                  co bude zaznamenáno datum platby.`)}
                  />
                  <br />
                  <br />
                </Grid>
              </WithBillingPlan>
              <WithBillingPlan plan={EBillingPlan.ONETIME}>
                <Grid item>
                  <Field
                    label={<b>{i18n._(t`Splatnost faktury`)} *</b>}
                    component={FormDateField}
                    name="invoiceDueDate"
                    helperText={i18n._(
                      t`Povinné datum splatnosti jednorázové faktury`,
                    )}
                  />
                  <br />
                  <br />
                </Grid>
              </WithBillingPlan>
              <Grid item>
                <Field
                  label={i18n._(t`Datum platby`)}
                  component={FormDateField}
                  name="invoicePaidAt"
                  inputProps={{ max: formatDateInput(new Date()) }}
                  helperText={i18n._(
                    t`Pokud není nastaveno, nebude možné využít účet, který vyčerpal trial platby.`,
                  )}
                />
              </Grid>
              <Grid item>
                <Field
                  label={i18n._(t`Identifikační číslo (IČ)`)}
                  component={FormTextField}
                  name="billingIdentity"
                  fullWidth
                />
                <br />
                <br />
              </Grid>
            </Grid>
          </WithBillingPlan>
        </Grid>
      </Formik>
    </Brick>
  )
}
